/**
 * Step3ChatPreview — Édition lignes + chat NL + création brouillon.
 *
 * Split-view : lignes éditables inline (gauche) + chat sidebar 400px (droite).
 *
 * Props :
 *   - state, setState
 *   - marginPct (memo dérivé calculé côté parent)
 *   - onUpdateLine(i, k, v), onAddLine(), onRemoveLine(i)
 *   - chatMessages, chatSending, chatInput, setChatInput
 *   - onSendChat(msg?), chatScrollRef
 *   - creating, onCreateDraft()
 *   - onBack()
 */
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft, Sparkles, Send, Loader2, CheckCircle2, Trash2, Plus,
  MessageSquare, Wand2, Eye,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import { eur, VAT_RATES, SUGGESTED, SUGGESTED_WITH_TEMPLATE, recomputeTotals } from "./utils";
import MarginControl from "./MarginControl";
import PdfLivePreview from "./PdfLivePreview";

export default function Step3ChatPreview({
  state, setState,
  marginPct, extractionDeltaPct,
  onUpdateLine, onAddLine, onRemoveLine,
  onApplyMargin,
  onPreviewPdf,
  selectedClient,
  chatMessages, chatSending, chatInput, setChatInput,
  onSendChat, chatScrollRef,
  creating, onCreateDraft,
  onBack,
}) {
  return (
    <div className="grid xl:grid-cols-[minmax(0,1fr)_420px_360px] lg:grid-cols-[minmax(0,1fr)_380px] gap-4">
      {/* Colonne gauche : lignes + totaux */}
      <div className="space-y-4">
        {/* Bandeau récap */}
        <Card className="border-violet-200 bg-gradient-to-r from-violet-50 to-fuchsia-50 rounded-xl p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div>
              <div className="text-xs text-slate-500">Source HT</div>
              <div className="font-mono font-semibold text-slate-700">
                {eur(state.source_total_ht || 0)}
              </div>
              {extractionDeltaPct !== null && Math.abs(Number(extractionDeltaPct)) > 0.5 && (
                <div className="text-[10px] text-amber-600 mt-0.5" title="Delta entre le total du PDF fournisseur et la somme des lignes extraites — peut indiquer une extraction incomplète (ex : frais oubliés)">
                  Δ extraction : {extractionDeltaPct}%
                </div>
              )}
            </div>
            <div>
              <div className="text-xs text-slate-500">Refacturé HT</div>
              <div className="font-mono font-semibold text-slate-900" data-testid="rebilling-current-ht">
                {eur(state.subtotal_ht || 0)}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Marge appliquée</div>
              <div className={cn(
                "font-mono font-semibold",
                marginPct === null || marginPct === "0.0" ? "text-slate-400" :
                Number(marginPct) > 0 ? "text-emerald-700" :
                Number(marginPct) < 0 ? "text-red-700" : "text-slate-500"
              )} data-testid="rebilling-margin-pct">
                {marginPct === null ? "—" : `${marginPct}%`}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500">TTC facturé</div>
              <div className="font-mono font-semibold text-slate-900" data-testid="rebilling-current-ttc">
                {eur(state.total_ttc || 0)}
              </div>
            </div>
          </div>
        </Card>

        <MarginControl
          onApply={onApplyMargin}
          currentMargin={marginPct}
          initialPct={state.template_detected_margin_percent}
        />

        <Card className="border-slate-200 bg-white rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <Label className="text-xs uppercase tracking-wider text-slate-500">
              Lignes ({(state.lines || []).length})
            </Label>
            <Button variant="ghost" size="sm" onClick={onAddLine}
                    className="text-violet-700 hover:bg-violet-50 h-7">
              <Plus className="h-3.5 w-3.5 mr-1" /> Ajouter
            </Button>
          </div>
          <div className="overflow-x-auto border border-slate-200 rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                <tr>
                  <th className="text-left px-2 py-1.5 min-w-[220px]">Désignation</th>
                  <th className="text-right px-2 py-1.5 min-w-[110px]">Qté</th>
                  <th className="text-right px-2 py-1.5 min-w-[130px]">PU HT</th>
                  <th className="text-right px-2 py-1.5 min-w-[90px]">TVA</th>
                  <th className="text-right px-2 py-1.5 min-w-[120px]">HT</th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {(state.lines || []).map((ln, i) => {
                  const net = Number(ln.quantity || 0) * Number(ln.unit_price_ht || 0);
                  return (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-2 py-1">
                        <Input value={ln.description}
                               onChange={(e) => onUpdateLine(i, "description", e.target.value)}
                               className="h-8 bg-white border-slate-200"
                               data-testid={`rebilling-line-desc-${i}`} />
                      </td>
                      <td className="px-2 py-1">
                        <Input type="number" min="0" step="1" value={ln.quantity}
                               onChange={(e) => onUpdateLine(i, "quantity", Number(e.target.value))}
                               className="h-8 text-right font-mono bg-white border-slate-200 w-full"
                               data-testid={`rebilling-line-qty-${i}`} />
                      </td>
                      <td className="px-2 py-1">
                        <Input type="number" min="0" step="0.0001" value={ln.unit_price_ht}
                               onChange={(e) => onUpdateLine(i, "unit_price_ht", Number(e.target.value))}
                               className="h-8 text-right font-mono bg-white border-slate-200 w-full"
                               data-testid={`rebilling-line-pu-${i}`} />
                      </td>
                      <td className="px-2 py-1">
                        <select id={`rebilling-line-vat-${i}`} name={`rebilling-line-vat-${i}`} value={ln.vat_rate}
                                onChange={(e) => onUpdateLine(i, "vat_rate", Number(e.target.value))}
                                className="h-8 w-full font-mono text-right bg-white border border-slate-200 rounded px-1">
                          {VAT_RATES.map((r) => <option key={r} value={r}>{r}%</option>)}
                        </select>
                      </td>
                      <td className="px-2 py-1 text-right font-mono tabular-nums whitespace-nowrap">{eur(net)}</td>
                      <td className="px-1 text-center">
                        {(state.lines || []).length > 1 && (
                          <button onClick={() => onRemoveLine(i)}
                                  className="text-slate-400 hover:text-red-600 p-1"
                                  aria-label="Supprimer">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex justify-end gap-6 text-sm">
            <span className="text-slate-600">HT <b className="font-mono ml-1">{eur(state.subtotal_ht || 0)}</b></span>
            <span className="text-slate-600">TVA <b className="font-mono ml-1">{eur(state.total_vat || 0)}</b></span>
            <span className="text-slate-900">TTC <b className="font-mono ml-1">{eur(state.total_ttc || 0)}</b></span>
          </div>

          <div className="mt-4 grid md:grid-cols-2 gap-3 pt-3 border-t border-slate-100">
            <div>
              <Label className="text-xs text-slate-500">Délai de paiement (jours)</Label>
              <Input type="number" value={state.payment_terms_days || 30}
                     onChange={(e) => setState((s) => ({ ...s, payment_terms_days: Number(e.target.value) }))}
                     className="mt-1 bg-white border-slate-200 font-mono h-8" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Remise globale %</Label>
              <Input type="number" step="0.1" value={state.global_discount_percent ?? ""}
                     onChange={(e) => setState((s) => recomputeTotals({
                       ...s,
                       global_discount_percent: e.target.value ? Number(e.target.value) : null,
                     }))}
                     placeholder="—"
                     className="mt-1 bg-white border-slate-200 font-mono h-8" />
            </div>
          </div>
          <div className="mt-3">
            <Label className="text-xs text-slate-500">Notes / mentions</Label>
            <Textarea value={state.notes || ""}
                      onChange={(e) => setState((s) => ({ ...s, notes: e.target.value }))}
                      className="mt-1 bg-white border-slate-200 min-h-[60px]" />
          </div>
        </Card>

        <div className="flex justify-between gap-3">
          <Button variant="ghost" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Retour
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onPreviewPdf}
                    className="border-violet-300 text-violet-700 hover:bg-violet-50"
                    data-testid="rebilling-preview-pdf-btn">
              <Eye className="h-4 w-4 mr-1" /> Aperçu PDF
            </Button>
            <Button
              onClick={onCreateDraft}
              disabled={creating || !(state.lines || []).length}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="rebilling-create-draft-btn"
            >
              {creating ? (
                <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Création…</>
              ) : (
                <><CheckCircle2 className="h-4 w-4 mr-1" /> Créer le brouillon</>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Colonne milieu (xl only) : aperçu PDF live avec debounce 500ms */}
      <div className="hidden xl:block">
        <PdfLivePreview state={state} client={selectedClient} />
      </div>

      {/* Colonne droite : chat */}
      <Card className="border-slate-200 bg-white rounded-xl flex flex-col overflow-hidden h-[720px]"
            data-testid="rebilling-chat-panel">
        <div className="p-3 border-b border-slate-200 flex items-center gap-2 bg-gradient-to-r from-violet-50 to-fuchsia-50">
          <div className="h-7 w-7 rounded-md bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
            <Sparkles className="h-3.5 w-3.5 text-white" />
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-900">Assistant refacturation</div>
            <div className="text-[10px] text-slate-500">Parle-lui en langage naturel</div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-3" ref={chatScrollRef}>
          {chatMessages.length === 0 && (
            <div className="text-sm text-slate-500 py-2">
              {state.template_invoice_id && (
                <div className="mb-4">
                  <div className="text-[10px] uppercase tracking-widest text-violet-600 font-semibold mb-2 flex items-center gap-1">
                    <Sparkles className="h-3 w-3" />
                    Actions template ({state.template_invoice_number || "précédente"})
                  </div>
                  <div className="flex flex-col gap-1.5 items-stretch">
                    {SUGGESTED_WITH_TEMPLATE.map((s) => (
                      <button key={s.label}
                              onClick={() => onSendChat(s.msg)}
                              className="px-2.5 py-1.5 text-xs text-left rounded-md border border-violet-200 bg-gradient-to-r from-violet-50 to-fuchsia-50 hover:border-violet-400 hover:from-violet-100 text-slate-800 font-medium"
                              data-testid={`rebilling-tpl-suggest-${s.label.replace(/[\s🎨🔀✂️💰➕:]+/g, '-').toLowerCase()}`}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="text-center">
                <MessageSquare className="h-6 w-6 mx-auto text-slate-300 mb-2" />
                <p className="mb-3 text-xs">
                  {state.template_invoice_id ? "Ou essaie une action rapide :" : "Essaie une consigne :"}
                </p>
                <div className="flex flex-col gap-1.5 items-stretch">
                  {SUGGESTED.map((s) => (
                    <button key={s.label}
                            onClick={() => onSendChat(s.msg)}
                            className="px-2.5 py-1.5 text-xs text-left rounded-md border border-slate-200 hover:border-violet-300 hover:bg-violet-50 text-slate-700"
                            data-testid={`rebilling-suggest-${s.label.replace(/\s+/g, '-').toLowerCase()}`}>
                      <Wand2 className="h-3 w-3 inline mr-1.5 text-violet-500" />
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          <AnimatePresence>
            {chatMessages.map((m, i) => (
              <motion.div key={i}
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={cn(
                            "max-w-[85%] rounded-lg p-2.5 text-sm",
                            m.role === "user"
                              ? "ml-auto bg-violet-600 text-white"
                              : m.error
                                ? "bg-red-50 text-red-800 border border-red-200"
                                : "bg-slate-100 text-slate-800"
                          )}
                          data-testid={`rebilling-chat-msg-${i}`}>
                <div className="whitespace-pre-wrap leading-snug">{m.text}</div>
                {(m.log || []).length > 0 && (
                  <div className="mt-2 pt-2 border-t border-slate-200/60 space-y-0.5">
                    {m.log.map((l, j) => (
                      <div key={j} className="text-[10px] text-slate-500 flex items-start gap-1">
                        <CheckCircle2 className="h-2.5 w-2.5 mt-0.5 shrink-0 text-emerald-600" />
                        <span>{l}</span>
                      </div>
                    ))}
                  </div>
                )}
                {/* Indicateur cross-reference matricules SOURCE ↔ TEMPLATE */}
                {m.role === "assistant" && !m.error && m.matricule_cross_ref && m.matricule_cross_ref.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-slate-200/60">
                    <div className="text-[10px] uppercase tracking-wider text-violet-600 font-semibold mb-1 flex items-center gap-1">
                      <Sparkles className="h-2.5 w-2.5" />
                      Match matricules template : {m.matricule_matches_count}/{m.matricule_cross_ref.length}
                    </div>
                    <div className="space-y-0.5 max-h-32 overflow-y-auto">
                      {m.matricule_cross_ref.map((x, k) => (
                        <div key={k} className={cn(
                          "text-[10px] flex items-start gap-1 px-1.5 py-0.5 rounded",
                          x.confidence === "high"
                            ? "bg-emerald-50 text-emerald-900"
                            : "bg-amber-50 text-amber-900"
                        )}
                        data-testid={`rebilling-xref-${k}`}>
                          <span className="font-mono shrink-0 opacity-60">#{x.source_index}</span>
                          {x.confidence === "high" ? (
                            <>
                              <CheckCircle2 className="h-2.5 w-2.5 mt-0.5 shrink-0 text-emerald-600" />
                              <span>
                                <b>{x.matched_matricule}</b> → « {x.template_match} »
                              </span>
                            </>
                          ) : (
                            <span>
                              Aucun match matricule
                              {x.source_matricules?.length > 0 && (
                                <span className="opacity-60"> (source: {x.source_matricules.join(", ")})</span>
                              )}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {m.role === "assistant" && !m.error && (m.count === 0) && (
                  <div className="text-[10px] text-amber-700 mt-1 italic">
                    Aucune action reconnue — reformule si besoin.
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>

          {chatSending && (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Loader2 className="h-3 w-3 animate-spin" /> L'IA réfléchit…
            </div>
          )}
        </div>

        <div className="p-2 border-t border-slate-200 flex gap-2">
          <Input
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && onSendChat()}
            placeholder="Ex: applique 20% de marge…"
            disabled={chatSending}
            className="bg-white border-slate-200"
            data-testid="rebilling-chat-input"
          />
          <Button size="sm"
                  onClick={() => onSendChat()}
                  disabled={chatSending || !chatInput.trim()}
                  className="bg-violet-600 hover:bg-violet-700 text-white"
                  data-testid="rebilling-chat-send">
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </Card>
    </div>
  );
}
